if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;

        // _wasDragged: set true during touch pan so a touchend-click does not unzoom
        this._wasDragged = false;

        document.body.appendChild(this);

        this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', () => this.hide());

        this.querySelector('.kore-lightbox__prev')?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.navigate(-1);
        });
        this.querySelector('.kore-lightbox__next')?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.navigate(1);
        });

        document.addEventListener('keydown', (e) => {
          if (e.code === 'Escape' && this.hasAttribute('open')) this.hide();
        });

        // Click on lightbox image: toggle zoom.
        // _wasDragged guard prevents accidental unzoom after a touch pan.
        const wrap = this.querySelector('.kore-lightbox__media-wrap');
        if (wrap) {
          wrap.addEventListener('click', (e) => {
            if (e.target.tagName === 'IMG') {
              if (this._wasDragged) { this._wasDragged = false; return; }
              this._toggleZoom(e.target);
            }
          });
        }

        this._setupGalleryDelegation();
        this._setupSwipe();
      }

      _setupSwipe() {
        const wrap = this.querySelector('.kore-lightbox__media-wrap');
        if (!wrap) return;
        let _sx = 0, _sy = 0;
        wrap.addEventListener('touchstart', (e) => {
          _sx = e.touches[0].clientX;
          _sy = e.touches[0].clientY;
        }, { passive: true });
        wrap.addEventListener('touchend', (e) => {
          if (this._wasDragged) return;
          const dx = e.changedTouches[0].clientX - _sx;
          const dy = e.changedTouches[0].clientY - _sy;
          if (Math.abs(dx) < 40) return;
          if (Math.abs(dy) > Math.abs(dx) * 0.7) return;
          // Swipe left (dx < 0) = next image; swipe right (dx > 0) = previous image
          this.navigate(dx < 0 ? 1 : -1);
        }, { passive: true });
      }

      _setupGalleryDelegation() {
        const secId = this.id.replace('ProductModal-', '');
        const attach = () => {
          const gallery = document.getElementById('MediaGallery-' + secId);
          if (!gallery) return;
          gallery.addEventListener('click', (e) => {
            if (this.hasAttribute('open')) return;
            const li = e.target.closest('.product__media-item');
            if (!li) return;
            e.stopPropagation();
            this._openFromGallery(gallery, li);
          }, true);
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', attach);
        } else {
          attach();
        }
      }

      _openFromGallery(gallery, clickedLi) {
        const visibleItems = [...gallery.querySelectorAll('li.product__media-item')]
          .filter(li => li.style.display !== 'none');
        if (!visibleItems.length) return;

        const imgItems = [];
        const imgSources = [];
        visibleItems.forEach(li => {
          const img = li.querySelector('img');
          if (!img) return;
          imgItems.push(li);
          imgSources.push({
            src: img.currentSrc || img.getAttribute('src') || '',
            alt: img.alt || ''
          });
        });
        if (!imgSources.length) return;

        let startIdx = imgItems.indexOf(clickedLi);
        if (startIdx < 0) startIdx = 0;
        this._dynImages = imgSources;
        this._dynIdx = startIdx;

        this._renderDynSlides();
        this._resetZoom();
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        this.querySelector('.kore-lightbox__media-wrap')?.focus();
      }

      // PUBLIC — Media Phase 1 hardening round. Lets an external gallery that already has its
      // own plain {src, alt} image list in hand (the Custom Sofa Configurator's combo-aware
      // gallery, kore-custom-media-gallery.js) open THIS SAME modal/lightbox showing those
      // exact images — no second modal, nothing rebuilt. Reuses the EXACT dynamic-slide
      // mechanism _openFromGallery() already uses internally for Dawn's own #MediaGallery-*
      // DOM scrape (_dynImages/_dynIdx/_renderDynSlides()/navigate()/swipe/zoom all already
      // built around a plain images array, never around real Shopify media ids) — this method
      // is purely additive, a new entry point into that SAME existing mechanism, given images
      // directly instead of scraped from a gallery DOM. Zero change to show()/_openFromGallery()
      // or any other existing method/behavior — every other caller of this modal (a normal PDP
      // gallery, if any exist elsewhere in the theme) is completely unaffected.
      showFromImages(images, startIdx) {
        if (!images || !images.length) return;
        this._dynImages = images;
        this._dynIdx = Math.max(0, Math.min(startIdx || 0, images.length - 1));
        this._renderDynSlides();
        this._resetZoom();
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        this.querySelector('.kore-lightbox__media-wrap')?.focus();
      }

      _renderDynSlides() {
        const wrap = this.querySelector('.kore-lightbox__media-wrap');
        if (!wrap || !this._dynImages) return;
        wrap.innerHTML = this._dynImages.map((img, i) =>
          '<div class="kore-lb-dyn__item' + (i === this._dynIdx ? ' active' : '') +
          '" data-kore-dyn-idx="' + i + '">' +
          '<img class="kore-lb-dyn__img"' +
          ' src="' + this._esc(img.src) + '"' +
          ' alt="' + this._esc(img.alt) + '"' +
          ' loading="' + (i === this._dynIdx ? 'eager' : 'lazy') + '">' +
          '</div>'
        ).join('');
      }

      _esc(s) {
        return String(s || '')
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      }

      show(opener) {
        this.openedBy = opener;
        const secId = this.id.replace('ProductModal-', '');
        const gallery = document.getElementById('MediaGallery-' + secId);
        if (gallery) {
          const mediaId = opener?.getAttribute('data-media-id');
          const li = mediaId
            ? gallery.querySelector('li[data-media-id="' + secId + '-' + mediaId + '"]') ||
              gallery.querySelector('li[data-media-id="' + mediaId + '"]')
            : null;
          const clickedLi = li ||
            gallery.querySelector('li.product__media-item.is-active') ||
            gallery.querySelector('li.product__media-item');
          if (clickedLi) {
            this._openFromGallery(gallery, clickedLi);
            return;
          }
        }
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        this._showStaticMedia(opener?.getAttribute('data-media-id'));
        this.querySelector('.kore-lightbox__media-wrap')?.focus();
      }

      _showStaticMedia(mediaId) {
        this.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('active'));
        if (!mediaId) return;
        const active = this.querySelector('[data-media-id="' + mediaId + '"]');
        if (!active) return;
        active.classList.add('active');
        if (active.nodeName === 'DEFERRED-MEDIA') {
          const tmpl = active.querySelector('template');
          if (tmpl?.content.querySelector('.js-youtube')) active.loadContent?.();
        }
      }

      hide() {
        document.body.classList.remove('overflow-hidden');
        this.removeAttribute('open');
        this._resetZoom();
        this._dynImages = null;
        window.pauseAllMedia?.();
        const opener = this.openedBy;
        this.openedBy = null;
        opener?.focus();
      }

      navigate(dir) {
        this._resetZoom();
        if (this._dynImages && this._dynImages.length > 1) {
          const wrap = this.querySelector('.kore-lightbox__media-wrap');
          if (!wrap) return;
          const items = [...wrap.querySelectorAll('.kore-lb-dyn__item')];
          const cur = items.findIndex(el => el.classList.contains('active'));
          const next = (cur + dir + this._dynImages.length) % this._dynImages.length;
          items[cur]?.classList.remove('active');
          items[next]?.classList.add('active');
          this._dynIdx = next;
        } else {
          const items = [...this.querySelectorAll('[data-media-id]')];
          if (items.length <= 1) return;
          const idx = items.findIndex((el) => el.classList.contains('active'));
          const next = (idx + dir + items.length) % items.length;
          items[idx]?.classList.remove('active');
          items[next]?.classList.add('active');
        }
      }

      _toggleZoom(img) {
        const wrap = this.querySelector('.kore-lightbox__media-wrap');
        if (!wrap) return;
        if (wrap.classList.contains('kore-lb--zoomed')) {
          this._resetZoom();
        } else {
          wrap.classList.add('kore-lb--zoomed');
          // Start zoom centered; transform-origin updates on mouse/touch move
          img.style.transformOrigin = '50% 50%';
          img.style.transform = 'scale(2)';
          img.style.cursor = 'crosshair';
          // Prevent browser native image drag (ghost image)
          img.draggable = false;
          this._attachZoom(wrap);
        }
      }

      _resetZoom() {
        this._wasDragged = false;
        const wrap = this.querySelector('.kore-lightbox__media-wrap');
        if (!wrap) return;
        wrap.classList.remove('kore-lb--zoomed');
        wrap.querySelectorAll('img').forEach(img => {
          img.style.transform = '';
          img.style.transformOrigin = '';
          img.style.cursor = '';
          img.draggable = true;
        });
        this._detachZoom(wrap);
      }

      // ── Zoom focus: transform-origin follows mouse/touch ───────────────────

      _attachZoom(wrap) {
        this._detachZoom(wrap);

        // Desktop: mousemove on the wrap updates transform-origin.
        // No click or drag required — just hover to pan the zoomed view.
        this._onMouseMove = (e) => {
          if (!wrap.classList.contains('kore-lb--zoomed')) return;
          const img = this._getActiveImg(wrap);
          if (!img) return;
          const rect = wrap.getBoundingClientRect();
          const xPct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
          const yPct = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
          img.style.transformOrigin = xPct + '% ' + yPct + '%';
        };

        // Prevent browser-native image drag (ghost square on mousedown+move)
        this._onDragStart = (e) => e.preventDefault();

        // Mobile: touchmove updates transform-origin the same way.
        // _wasDragged prevents a touchend-fired click from unzooming.
        this._onTouchMove = (e) => {
          if (!wrap.classList.contains('kore-lb--zoomed')) return;
          const img = this._getActiveImg(wrap);
          if (!img) return;
          const t = e.touches[0];
          const rect = wrap.getBoundingClientRect();
          const xPct = Math.max(0, Math.min(100, ((t.clientX - rect.left) / rect.width) * 100));
          const yPct = Math.max(0, Math.min(100, ((t.clientY - rect.top) / rect.height) * 100));
          img.style.transformOrigin = xPct + '% ' + yPct + '%';
          this._wasDragged = true;
          e.preventDefault(); // suppress page scroll while panning
        };

        wrap.addEventListener('mousemove', this._onMouseMove);
        wrap.addEventListener('dragstart', this._onDragStart);
        wrap.addEventListener('touchmove', this._onTouchMove, { passive: false });
      }

      _detachZoom(wrap) {
        if (wrap) {
          if (this._onMouseMove) wrap.removeEventListener('mousemove', this._onMouseMove);
          if (this._onDragStart) wrap.removeEventListener('dragstart', this._onDragStart);
          if (this._onTouchMove) wrap.removeEventListener('touchmove', this._onTouchMove);
        }
        this._onMouseMove = this._onDragStart = this._onTouchMove = null;
      }

      _getActiveImg(wrap) {
        return wrap.querySelector('.kore-lb-dyn__item.active img') ||
               wrap.querySelector('img[data-media-id].active') ||
               null;
      }
    }
  );
}
